import { AudioOutlined, CloseOutlined, FilePdfOutlined, InboxOutlined } from "@ant-design/icons";
import { Button, Col, Tabs, Typography, Upload, UploadProps, message } from "antd";
import Dragger from "antd/es/upload/Dragger";
import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import styles from "./createnotemodal.module.scss";

const MAX_AUDIO_UPLOAD_SIZE = 100 * 1024 * 1024; // 100MB limit for audio files

// PDF transcription falls back from Gemini to Claude when Gemini errors for any
// reason, and Claude/Anthropic caps a request at 32MB and 600 pages across all
// attached PDFs combined — reject here so a file that would only work on Gemini
// never gets accepted, and never reaches the backend just to fail once it falls back.
const MAX_PDF_TOTAL_SIZE = 32 * 1024 * 1024;
const MAX_PDF_TOTAL_PAGES = 600;

/**
 * Cheap page-count estimate: counts `/Type /Page` object markers in the raw bytes.
 * No PDF parsing library in this app, and pulling one in just for a page count
 * isn't worth it. Works for the vast majority of real-world PDFs; returns null
 * (never blocks) for anything using compressed cross-reference streams where the
 * markers aren't in plain text — that's still safe, since the size check and the
 * backend's error handling remain as a fallback for anything this misses.
 */
async function getPdfPageCount(file: File): Promise<number | null> {
  try {
    const buffer = await file.arrayBuffer();
    const text = new TextDecoder("latin1").decode(buffer);
    const matches = text.match(/\/Type\s*\/Page[^s]/g);
    return matches && matches.length > 0 ? matches.length : null;
  } catch {
    return null;
  }
}

const FileUploader = ({
  audioFileRef,
  pendingPdfFiles = [],
  onPendingPdfFilesChange,
  onUploadTypeChange,
}: {
  audioFileRef: React.MutableRefObject<any>;
  pendingPdfFiles?: File[];
  onPendingPdfFilesChange?: (files: File[]) => void;
  onUploadTypeChange?: (type: "audio" | "pdf") => void;
}) => {
  const dispatch = useAppDispatch();
  const componentName = "createnotemodal";
  const [uploadType, setUploadType] = useState<"audio" | "pdf">("audio");

  const { recordingDetail } = useAppSelector((state) => state.recordings);

  const isAudioFile = (file: File): boolean => {
    // Check MIME type
    if (file.type && !file.type.startsWith("audio/")) {
      return false;
    }

    // Additional check for common audio file extensions if MIME type is not set
    const audioExtensions = [".mp3", ".m4a", ".wav", ".webm", ".ogg", ".aac", ".flac", ".opus"];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));

    if (!file.type && !audioExtensions.includes(fileExtension)) {
      return false;
    }

    return true;
  };

  const audioProps: UploadProps = {
    name: "file",
    accept: "audio/*",
    multiple: false,
    showUploadList: false,
    onChange(info) {
      //@ts-ignore
      audioFileRef.current = info.file;
      return false;
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
    beforeUpload(file) {
      // Validate it's an audio file
      if (!isAudioFile(file)) {
        message.error("Only audio files are allowed. Supported formats: MP3, M4A, WAV, WEBM, OGG");
        return Upload.LIST_IGNORE;
      }

      // Check audio file size limit (100MB)
      if (file.size > MAX_AUDIO_UPLOAD_SIZE) {
        message.error(`Audio file exceeds 100MB limit. File size: ${formatFileSize(file.size)}`);
        return Upload.LIST_IGNORE;
      }

      dispatch(setRecordingDetail({ ...recordingDetail, fileList: [file] }));
      return false;
    },
    fileList: recordingDetail.fileList || [],
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  };

  const handlePDFUpload = async (file: File, fileList: File[]) => {
    const MAX_PDF_COUNT = 3;

    // Validate individual file immediately
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      message.error(`Only PDF files are allowed. Invalid file: ${file.name}`);
      return Upload.LIST_IGNORE;
    }

    // Only process when all files are added (last file in the batch)
    const isLastFile = fileList[fileList.length - 1] === file;
    if (!isLastFile) {
      return false;
    }

    // Check current total PDFs (pending)
    const currentPdfCount = pendingPdfFiles.length;
    const availableSlots = MAX_PDF_COUNT - currentPdfCount;

    if (availableSlots <= 0) {
      message.error(`Maximum of ${MAX_PDF_COUNT} PDFs allowed. Please remove existing PDFs to upload new ones.`);
      return false;
    }

    // Validate all files
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    for (const f of fileList) {
      if (!f.name.toLowerCase().endsWith(".pdf")) {
        invalidFiles.push(`${f.name}: Only PDF files allowed`);
        continue;
      }
      if (f.size > MAX_PDF_TOTAL_SIZE) {
        invalidFiles.push(`${f.name}: File too large (max 32MB)`);
        continue;
      }
      validFiles.push(f);
    }

    // Limit valid files to available slots
    if (validFiles.length > availableSlots) {
      const extraCount = validFiles.length - availableSlots;
      validFiles.splice(availableSlots);
      invalidFiles.push(`${extraCount} file(s) skipped: Maximum ${MAX_PDF_COUNT} PDFs allowed`);
    }

    // Combined size and page count across pending + newly-added PDFs must also stay
    // under the limit — both are per-request caps shared by all attached documents,
    // not just per-file.
    const pendingPageCounts = await Promise.all(pendingPdfFiles.map(getPdfPageCount));
    let totalSize = pendingPdfFiles.reduce((sum, f) => sum + f.size, 0);
    let totalPages = pendingPageCounts.reduce((sum: number, c) => sum + (c ?? 0), 0);

    const acceptedFiles: File[] = [];
    for (const f of validFiles) {
      if (totalSize + f.size > MAX_PDF_TOTAL_SIZE) {
        invalidFiles.push(`${f.name}: Skipped — combined PDF size would exceed 32MB`);
        continue;
      }
      const pageCount = await getPdfPageCount(f);
      if (pageCount !== null && totalPages + pageCount > MAX_PDF_TOTAL_PAGES) {
        invalidFiles.push(`${f.name}: Skipped — combined page count would exceed ${MAX_PDF_TOTAL_PAGES} pages`);
        continue;
      }
      totalSize += f.size;
      totalPages += pageCount ?? 0;
      acceptedFiles.push(f);
    }
    validFiles.length = 0;
    validFiles.push(...acceptedFiles);

    if (invalidFiles.length > 0) {
      message.error(
        <div>
          <div>Some files were skipped:</div>
          {invalidFiles.map((msg, i) => (
            <div key={i} style={{ fontSize: "12px" }}>
              • {msg}
            </div>
          ))}
        </div>,
        5,
      );
    }

    if (validFiles.length === 0) {
      return false;
    }

    // Store files for upload when Generate Note is clicked
    const updatedPendingFiles = [...pendingPdfFiles, ...validFiles];
    onPendingPdfFilesChange?.(updatedPendingFiles);
    message.success(`${validFiles.length} PDF${validFiles.length > 1 ? "s" : ""} ready to upload`);

    return false;
  };

  const handleRemovePendingFile = (index: number) => {
    const updatedFiles = pendingPdfFiles.filter((_, i) => i !== index);
    onPendingPdfFilesChange?.(updatedFiles);
  };

  const handleRemoveAudioFile = () => {
    audioFileRef.current = null;
    dispatch(setRecordingDetail({ ...recordingDetail, fileList: [] }));
  };

  const MAX_PDF_COUNT = 3;
  const currentPdfCount = pendingPdfFiles.length;
  const pdfUploadDisabled = currentPdfCount >= MAX_PDF_COUNT;

  const pdfProps: UploadProps = {
    name: "files",
    accept: ".pdf",
    multiple: true,
    showUploadList: false,
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);

      // Validate dropped files immediately
      const files = Array.from(e.dataTransfer.files);
      const nonPdfFiles = files.filter((f: File) => !f.name.toLowerCase().endsWith(".pdf"));

      if (nonPdfFiles.length > 0) {
        const fileNames = nonPdfFiles.map((f: File) => f.name).join(", ");
        message.error(`Only PDF files are allowed. Invalid file(s): ${fileNames}`);
      }
    },
    beforeUpload: handlePDFUpload,
    disabled: pdfUploadDisabled,
  };

  return (
    <Col span={24} className={`${styles[`${componentName}__modal-row`]}`} style={{ marginTop: "15px" }}>
      <Tabs
        activeKey={uploadType}
        onChange={(key) => {
          const newType = key as "audio" | "pdf";
          setUploadType(newType);
          onUploadTypeChange?.(newType);
        }}
        items={[
          {
            key: "audio",
            label: "Audio",
            children: (
              <div>
                <div
                  style={{
                    padding: "12px",
                    background: "#e6f4ff",
                    border: "1px solid #91caff",
                    borderRadius: "4px",
                    marginBottom: "12px",
                  }}
                >
                  <Typography.Text style={{ color: "#0958d9", fontSize: "14px" }}>
                    💡 Upload your session recording to generate clinical notes.
                  </Typography.Text>
                </div>

                {/* Show selected audio file */}
                {recordingDetail.fileList && recordingDetail.fileList.length > 0 && (
                  <div style={{ marginBottom: "12px" }}>
                    {recordingDetail.fileList.map((file: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px 12px",
                          background: "#e6f7ff",
                          borderRadius: "4px",
                          marginBottom: "8px",
                          border: "1px dashed #91d5ff",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                          <AudioOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Typography.Text
                              ellipsis={{ tooltip: file.name }}
                              style={{ display: "block", fontWeight: 500 }}
                            >
                              {file.name}
                            </Typography.Text>
                            <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                              {formatFileSize(file.size)} • Will upload when Generate Note is clicked
                            </Typography.Text>
                          </div>
                        </div>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={handleRemoveAudioFile}
                          style={{ marginLeft: "8px" }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <Dragger {...audioProps}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag audio file to this area to upload</p>
                  <p className="ant-upload-hint">
                    Single audio file upload. Supported formats: MP3, M4A, WAV, WEBM, OGG. Max size: 100MB
                  </p>
                </Dragger>
              </div>
            ),
          },
          {
            key: "pdf",
            label: "PDFs",
            children: (
              <div>
                <div
                  style={{
                    padding: "12px",
                    background: "#e6f4ff",
                    border: "1px solid #91caff",
                    borderRadius: "4px",
                    marginBottom: "12px",
                  }}
                >
                  <Typography.Text style={{ color: "#0958d9", fontSize: "14px" }}>
                    💡 Establish patient context by uploading referral documents, prior assessments, etc..
                  </Typography.Text>
                </div>

                {/* Show pending files (will upload when Generate Note is clicked) */}
                {pendingPdfFiles.length > 0 && (
                  <div style={{ marginBottom: "12px" }}>
                    {pendingPdfFiles.map((file, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px 12px",
                          background: "#e6f7ff",
                          borderRadius: "4px",
                          marginBottom: "8px",
                          border: "1px dashed #91d5ff",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                          <FilePdfOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Typography.Text
                              ellipsis={{ tooltip: file.name }}
                              style={{ display: "block", fontWeight: 500 }}
                            >
                              {file.name}
                            </Typography.Text>
                            <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                              {formatFileSize(file.size)} • Will upload when Generate Note is clicked
                            </Typography.Text>
                          </div>
                        </div>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() => handleRemovePendingFile(index)}
                          style={{ marginLeft: "8px" }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <Dragger {...pdfProps}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {pdfUploadDisabled && currentPdfCount >= MAX_PDF_COUNT
                      ? `Maximum ${MAX_PDF_COUNT} PDFs reached`
                      : "Click or drag PDF files to this area to upload"}
                  </p>
                  <p className="ant-upload-hint">
                    {pdfUploadDisabled && currentPdfCount >= MAX_PDF_COUNT
                      ? `Remove existing PDFs to upload new ones (${currentPdfCount}/${MAX_PDF_COUNT} uploaded)`
                      : `Maximum ${MAX_PDF_COUNT} PDFs allowed. Supported formats: PDF. Max combined size: 32MB, 600 pages (${currentPdfCount}/${MAX_PDF_COUNT} uploaded)`}
                  </p>
                </Dragger>
              </div>
            ),
          },
        ]}
      />
    </Col>
  );
};

export default FileUploader;
